/**
 * Prestige — la boucle, pas la fin (§6, feuille de route étape 7). **Logique pure.**
 *
 * ══ Pourquoi ce module est le manquement n° 1 du jeu ═════════════════════════
 *
 * Le principe premier du §1 est absolu : **il ne doit jamais exister d'état « j'ai
 * fini »**. Or le terrain est plafonné à douze extensions, et l'audit du 17 août l'a
 * mesuré : **les douze sont achetées en 4,3 h de jeu actif**. Passé ce point, la grille
 * est pleine, le catalogue est épuisé, et le joueur n'a plus rien à viser. Le plafond du
 * terrain devait « déboucher sur le prestige » — le prestige n'existait pas.
 *
 * ══ Trois décisions structurantes ════════════════════════════════════════════
 *
 * **Le multiplicateur s'ACCUMULE, il ne se remplace pas.** Chaque ère ajoute ses étoiles
 * au multiplicateur permanent. C'est ce qui rend la répétition désirable : la deuxième
 * ère va deux fois plus vite que la première, la troisième plus vite encore. Un
 * multiplicateur recalculé à chaque prestige pourrait BAISSER après une ère courte, ce
 * qui punirait le joueur d'avoir prestigé — l'inverse exact de l'intention.
 *
 * **Le gain se mesure sur l'ÈRE, pas sur toute la partie.** `totalProduced` est cumulatif
 * depuis toujours, prestiges compris (c'est la source des jalons et des ligues, et il ne
 * doit jamais reculer). Le prestige a donc besoin de savoir où l'ère a commencé :
 * `eraStartedAt` retient la valeur de `totalProduced` au dernier prestige. Sans ce
 * repère, le seuil serait franchi d'emblée après le premier prestige et le joueur
 * pourrait prestiger en boucle sans jamais rien produire.
 *
 * **La racine cubique, et pas la racine carrée.** Une ère produit des ordres de grandeur,
 * pas des multiples : sans compression forte, le premier prestige rapporterait un
 * facteur trente et rendrait tout ce qui suit trivial. La racine cubique donne ×2 au
 * premier prestige — vers deux à quatre heures de jeu, la cible du §6 — puis une
 * progression qui ne s'emballe jamais.
 *
 * ══ Ce qui survit, et pourquoi ═══════════════════════════════════════════════
 *
 * Survivent les **gemmes** (elles se gagnent par jalons, pas par production : les perdre
 * punirait un effort qui n'a rien à voir), le **niveau et le multiplicateur**, les
 * **jalons déjà encaissés** (les re-créditer serait une machine à gemmes), les
 * **compteurs cumulés** des quêtes, et les **monuments d'héritage** (§16.4 — c'est leur
 * raison d'être). Repartent à zéro : pièces, bâtiments, routes, terrain.
 */

import { ECONOMY, PRESTIGE } from '@/config/balance';

/** Ce que le prestige observe. Volontairement minimal : deux nombres suffisent. */
export interface PrestigeProgress {
  /** Pièces cumulées depuis toujours, prestiges compris. */
  readonly totalProduced: number;
  /** Valeur de `totalProduced` au début de l'ère en cours. */
  readonly eraStartedAt: number;
}

/** Production de l'ÈRE en cours — la seule qui compte pour le gain. */
export function producedThisEra(progress: PrestigeProgress): number {
  return Math.max(0, progress.totalProduced - progress.eraStartedAt);
}

/**
 * Étoiles gagnées si l'on prestigeait maintenant.
 *
 * `cbrt` et non `sqrt` : une ère produit des ordres de grandeur. À 10⁹ pièces produites
 * — l'ordre de grandeur d'une première partie de deux à quatre heures — on obtient dix
 * étoiles, soit ×2. La racine carrée en aurait donné trente-et-une, et la partie
 * suivante n'aurait plus eu d'intérêt.
 *
 * Le plancher est un entier : une récompense fractionnaire (« 3,7 étoiles ») ne se lit
 * pas comme un accomplissement.
 */
export function starsFor(progress: PrestigeProgress): number {
  const produced = producedThisEra(progress);
  if (produced <= 0) return 0;
  return Math.floor(Math.cbrt(produced / PRESTIGE.scale));
}

/** Multiplicateur permanent qu'ajouteraient ces étoiles. */
export function multiplierGain(stars: number): number {
  return Math.max(0, stars) * PRESTIGE.perStar;
}

/**
 * Le prestige est-il disponible ?
 *
 * On exige plusieurs étoiles, pas une seule. À une étoile le gain serait de +10 %, ce
 * qui ne compense jamais la perte de toute une ville : le joueur qui accepterait
 * regretterait, et celui qui refuse verrait un bouton actif qu'il ne doit pas presser.
 * Un seuil qui rend l'offre toujours BONNE vaut mieux qu'un seuil qui la rend toujours
 * possible.
 */
export function canPrestige(progress: PrestigeProgress): boolean {
  return starsFor(progress) >= PRESTIGE.minStars;
}

/** Ce qu'il reste à produire avant de pouvoir prestiger. Zéro si c'est déjà le cas. */
export function remainingToPrestige(progress: PrestigeProgress): number {
  const needed = PRESTIGE.scale * PRESTIGE.minStars ** 3;
  return Math.max(0, needed - producedThisEra(progress));
}

/** Aperçu affiché avant de confirmer — le joueur décide en connaissance de cause. */
export interface PrestigePreview {
  readonly available: boolean;
  readonly stars: number;
  readonly gain: number;
  /** Multiplicateur qu'aurait le joueur après le prestige. */
  readonly nextMultiplier: number;
  /** Production de l'ère, pour l'afficher telle quelle. */
  readonly produced: number;
  /** Pièces restant à produire avant que ce soit possible. */
  readonly remaining: number;
}

export function prestigePreview(
  progress: PrestigeProgress,
  currentMultiplier: number,
): PrestigePreview {
  const stars = starsFor(progress);
  const gain = multiplierGain(stars);

  return {
    available: stars >= PRESTIGE.minStars,
    stars,
    gain,
    nextMultiplier: currentMultiplier + gain,
    produced: producedThisEra(progress),
    remaining: remainingToPrestige(progress),
  };
}

/** Pièces de départ d'une ère neuve. Identiques au tout premier lancement (§6). */
export const ERA_STARTING_COINS = ECONOMY.startingCoins;
